import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Star } from 'lucide-react';
import SEO from '../components/SEO';
import { FEATURE_PAGES, OTHER_FEATURES } from '../data/featurePages';
import { trackPageView, trackDownloadClick } from '../lib/track';

const IOS_LINK = 'https://apps.apple.com/us/app/couplein/id6749887412';
const ANDROID_LINK = 'https://play.google.com/store/apps/details?id=dev.expo.couplein&pcampaignid=web_share';
const LOGO_URL = '/couplein_logo.png';

import Navbar from '../components/Navbar';

const StoreButtons = ({ dark = false, className = '', trackId }) => (
  <div className={`flex flex-col sm:flex-row gap-3 ${className}`}>
    {[
      { href: IOS_LINK, small: 'Download on the', big: 'App Store', testid: 'fl-ios-btn', icon: 'M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z' },
      { href: ANDROID_LINK, small: 'Get it on', big: 'Google Play', testid: 'fl-android-btn', icon: 'M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z' },
    ].map(({ href, small, big, testid, icon }) => (
      <a key={testid} href={href} target="_blank" rel="noopener noreferrer" data-testid={testid}
        onClick={() => trackId && trackDownloadClick(trackId)}
        className={`flex items-center justify-center gap-2 rounded-xl transition-all hover:scale-105 active:scale-95 px-6 py-3.5 ${dark ? 'bg-white text-gray-900 hover:bg-gray-100' : 'bg-black text-white hover:bg-gray-800'}`}>
        <svg className="w-7 h-7" viewBox="0 0 24 24" fill="currentColor"><path d={icon} /></svg>
        <div className="text-left">
          <p className="text-[10px] opacity-80">{small}</p>
          <p className="font-semibold text-base -mt-0.5">{big}</p>
        </div>
      </a>
    ))}
  </div>
);

export default function FeatureLanding({ pageId }) {
  const page = FEATURE_PAGES[pageId];
  const others = OTHER_FEATURES.filter((f) => f.id !== pageId);

  useEffect(() => { trackPageView(pageId); }, [pageId]);

  return (
    <div className="min-h-screen bg-white">
      <SEO title={page.seo.title} description={page.seo.description} keywords={page.seo.keywords} path={page.path} />

      {/* Unified Public Navbar */}
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-gradient-to-b from-pink-50 via-white to-white">
        <div className="max-w-6xl mx-auto px-4 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center lg:text-left">
            <span className="inline-block bg-pink-100 text-pink-600 text-sm font-semibold px-4 py-1.5 rounded-full mb-6">{page.badge}</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-gray-900 leading-tight mb-6" data-testid="fl-headline">
              {page.h1a}{' '}
              <span className="bg-gradient-to-r from-pink-500 to-orange-500 bg-clip-text text-transparent">{page.h1b}</span>
            </h1>
            <p className="text-lg text-gray-600 mb-8 max-w-xl mx-auto lg:mx-0">{page.sub}</p>
            <StoreButtons className="justify-center lg:justify-start" trackId={pageId} />
            <div className="flex items-center justify-center lg:justify-start gap-4 mt-6 text-sm text-gray-500">
              <span className="flex items-center gap-1"><Star className="w-4 h-4 text-amber-400 fill-amber-400" /> 4.9 Rating</span>
              <span>10K+ Downloads</span>
              <span>Free · 60-sec setup</span>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.15 }} className="flex justify-center">
            <div className="relative bg-gray-900 rounded-[2.5rem] p-2.5 shadow-2xl w-[250px] sm:w-[290px]">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 bg-gray-900 rounded-b-2xl w-28 h-6 z-20" />
              <img src={page.heroShot} alt={page.heroAlt} className="block w-full rounded-[2rem]" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 max-w-6xl mx-auto px-4">
        <div className="grid md:grid-cols-3 gap-8">
          {page.benefits.map(({ emoji, title, text }, i) => (
            <motion.div key={title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ delay: i * 0.1 }} className="bg-gray-50 rounded-3xl p-8">
              <div className="text-4xl mb-4">{emoji}</div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">{title}</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Other features */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">{page.othersTitle}</h2>
          <p className="text-gray-500 mb-12">{page.othersSub}</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
            {others.map(({ id, shot, title, text }) => (
              <Link key={id} to={`/${id}`} data-testid={`fl-other-${id}`}
                className="block group bg-white rounded-3xl p-6 shadow-sm hover:shadow-lg transition-shadow">
                <img src={shot} alt={`CoupleIn ${title}`} loading="lazy"
                  className="w-full max-w-[180px] mx-auto rounded-2xl border border-gray-100 shadow-md mb-5 aspect-[9/16] object-cover object-top group-hover:scale-105 transition-transform duration-300" />
                <h3 className="font-bold text-gray-900 mb-1 group-hover:text-pink-500 transition-colors">{title}</h3>
                <p className="text-sm text-gray-600">{text}</p>
                <p className="text-xs text-pink-500 font-medium mt-2">Learn more →</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 7-Day Challenge close */}
      <section className="py-20 bg-gradient-to-br from-gray-950 via-gray-900 to-pink-950 text-white text-center px-4">
        <div className="max-w-3xl mx-auto">
          <p className="inline-block bg-pink-500/15 border border-pink-500/40 text-pink-300 text-xs sm:text-sm font-bold uppercase tracking-widest px-4 py-2 rounded-full mb-6">
            ⚡ The CoupleIn 7-Day Challenge
          </p>
          <h2 className="text-3xl sm:text-5xl font-extrabold leading-tight mb-5">
            Try CoupleIn for 7 days.
            <br />
            <span className="bg-gradient-to-r from-pink-400 to-orange-400 bg-clip-text text-transparent">
              If your relationship doesn't improve, uninstall it.
            </span>
          </h2>
          <p className="text-gray-300 mb-8">That's the deal. No card. No excuses. Just 7 days.</p>
          <div className="flex justify-center"><StoreButtons dark trackId={pageId} /></div>
          <p className="text-xs text-gray-500 mt-4">Worst case, you delete an app. Best case, you change your relationship.</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 border-t border-gray-800 text-gray-400 text-sm py-8 text-center">
        <div className="flex justify-center gap-6 mb-4">
          <Link to="/" className="hover:text-white transition-colors">Home</Link>
          <Link to="/blog" className="hover:text-white transition-colors">Blog</Link>
          <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
          <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
        </div>
        <p>© {new Date().getFullYear()} Couple In LTD · Company No. 16342045 · Registered in England &amp; Wales.</p>
      </footer>
    </div>
  );
}
